import styled from "@emotion/styled";
import { FC, useState } from "react";
import { FieldInputProps, useForm } from "react-final-form";

import { Edit } from "@livechat/design-system-icons";
import { Button, DesignToken, Icon, SpacingToken } from "@livechat/design-system-react-components";

import { ChooseActionOrTriggerModal } from "routes/automations/shared/components/ChooseActionOrTriggerModal";
import {
  IRuleTriggerAgentChanged,
  IRuleTriggerAgentID,
  IRuleTriggerDetailedSourceType,
  IRuleTriggerEventCount,
  IRuleTriggerFollowerAdded,
  IRuleTriggerFollowerID,
  IRuleTriggerFollowerRemoved,
  IRuleTriggerHasAssignee,
  IRuleTriggerLanguage,
  IRuleTriggerNewAgentMessage,
  IRuleTriggerNewMessage,
  IRuleTriggerPriority,
  IRuleTriggerPriorityChanged,
  IRuleTriggerRating,
  IRuleTriggerRatingChanged,
  IRuleTriggerRequesterEmail,
  IRuleTriggerSecondsPassedSinceEvent,
  IRuleTriggerSourceMailbox,
  IRuleTriggerStatus,
  IRuleTriggerStatusChanged,
  IRuleTriggerSubject,
  IRuleTriggerTagAdded,
  IRuleTriggerTagID,
  IRuleTriggerTagRemoved,
  IRuleTriggerTeamChanged,
  IRuleTriggerTeamID,
} from "store/entities/rules/interfaces";

import { TriggerFieldArrayElement, WorkflowConditionType } from "../../interfaces";

import { AgentChangedTrigger } from "./parts/AgentChangedTrigger";
import { AgentTrigger } from "./parts/AgentTrigger";
import { EventCountTrigger } from "./parts/EventCountTrigger";
import { FollowerTrigger } from "./parts/FollowerTrigger";
import { ForwardingTrigger } from "./parts/ForwardingTrigger";
import { LanguageTrigger } from "./parts/LanguageTrigger";
import { MessageTrigger } from "./parts/MessageTrigger";
import { PriorityChangedTrigger } from "./parts/PriorityChangedTrigger";
import { PriorityTrigger } from "./parts/PriorityTrigger";
import { RatingTrigger } from "./parts/RatingTrigger";
import { RequesterTrigger } from "./parts/RequesterTrigger";
import { SourceTypeTrigger } from "./parts/SourceTypeTrigger";
import { StatusChangedTrigger } from "./parts/StatusChangedTrigger";
import { StatusTrigger } from "./parts/StatusTrigger";
import { TagTrigger } from "./parts/TagTrigger";
import { TeamChangedTrigger } from "./parts/TeamChangedTrigger";
import { TeamTrigger } from "./parts/TeamTrigger";
import { TimePassedSinceTrigger } from "./parts/TimePassedSinceEventTrigger";
import { TRIGGER_NAME, changeTrigger, defaultTriggers, isTriggerType, mapTriggerToTriggerType } from "./utils";

interface IProps {
  input: FieldInputProps<TriggerFieldArrayElement>;
}

const TriggerElement: FC<IProps> = ({ input }) => {
  if (!input.value?.type) {
    return null;
  }

  if (isTriggerType(input, "isNew") || isTriggerType(input, "isSpam")) {
    return null;
  }

  if (isTriggerType<IRuleTriggerDetailedSourceType>(input, "detailedSourceType")) {
    return <SourceTypeTrigger input={input} />;
  }

  if (isTriggerType<IRuleTriggerStatus>(input, "status")) {
    return <StatusTrigger input={input} />;
  }

  if (isTriggerType<IRuleTriggerStatusChanged>(input, "statusChanged")) {
    return <StatusChangedTrigger input={input} />;
  }

  if (isTriggerType<IRuleTriggerPriority>(input, "priority")) {
    return <PriorityTrigger input={input} />;
  }

  if (isTriggerType<IRuleTriggerPriorityChanged>(input, "priorityChanged")) {
    return <PriorityChangedTrigger input={input} />;
  }

  if (
    isTriggerType<IRuleTriggerRating>(input, "rating") ||
    isTriggerType<IRuleTriggerRatingChanged>(input, "ratingChanged")
  ) {
    return <RatingTrigger input={input} />;
  }

  if (
    isTriggerType<IRuleTriggerNewMessage>(input, "newMessage") ||
    isTriggerType<IRuleTriggerNewAgentMessage>(input, "newAgentMessage") ||
    isTriggerType<IRuleTriggerSubject>(input, "subject")
  ) {
    return <MessageTrigger input={input} />;
  }

  if (
    isTriggerType<IRuleTriggerTagAdded>(input, "tagAdded") ||
    isTriggerType<IRuleTriggerTagRemoved>(input, "tagRemoved") ||
    isTriggerType<IRuleTriggerTagID>(input, "tagID")
  ) {
    return <TagTrigger input={input} />;
  }

  if (isTriggerType<IRuleTriggerSourceMailbox>(input, "sourceMailbox")) {
    return <ForwardingTrigger input={input} />;
  }

  if (isTriggerType<IRuleTriggerTeamID>(input, "teamID")) {
    return <TeamTrigger input={input} />;
  }

  if (isTriggerType<IRuleTriggerTeamChanged>(input, "teamChanged")) {
    return <TeamChangedTrigger input={input} />;
  }

  if (
    isTriggerType<IRuleTriggerAgentID>(input, "agentID") ||
    isTriggerType<IRuleTriggerHasAssignee>(input, "hasAssignee")
  ) {
    return <AgentTrigger input={input} />;
  }

  if (isTriggerType<IRuleTriggerAgentChanged>(input, "agentChanged")) {
    return <AgentChangedTrigger input={input} />;
  }

  if (
    isTriggerType<IRuleTriggerFollowerID>(input, "followerID") ||
    isTriggerType<IRuleTriggerFollowerAdded>(input, "followerAdded") ||
    isTriggerType<IRuleTriggerFollowerRemoved>(input, "followerRemoved")
  ) {
    return <FollowerTrigger input={input} />;
  }

  if (isTriggerType<IRuleTriggerRequesterEmail>(input, "requesterEmail")) {
    return <RequesterTrigger input={input} />;
  }

  if (isTriggerType<IRuleTriggerSecondsPassedSinceEvent>(input, "secondsPassedSinceEvent")) {
    return <TimePassedSinceTrigger input={input} />;
  }

  if (isTriggerType<IRuleTriggerLanguage>(input, "language")) {
    return <LanguageTrigger input={input} />;
  }

  if (isTriggerType<IRuleTriggerEventCount>(input, "eventCount")) {
    return <EventCountTrigger input={input} />;
  }

  return null;
};

interface ITriggerProps {
  input: FieldInputProps<TriggerFieldArrayElement>;
}

export const Trigger: FC<ITriggerProps> = ({ input }) => {
  const form = useForm();

  const [editModalOpen, setEditModalOpen] = useState(false);

  const handleSelectTrigger = (type: WorkflowConditionType): void => {
    form.resetFieldState(input.name);
    changeTrigger(input, defaultTriggers[type]);
    setEditModalOpen(false);
  };

  const triggerType = mapTriggerToTriggerType(input.value);

  const triggerElement = TriggerElement({ input });

  return (
    <>
      <Button
        kind="secondary"
        icon={<Icon source={Edit} />}
        iconPosition="right"
        onClick={() => setEditModalOpen(true)}
      >
        {triggerType && TRIGGER_NAME[triggerType]}
      </Button>
      {triggerElement && <TriggerContainer>{triggerElement}</TriggerContainer>}
      <ChooseActionOrTriggerModal
        type="trigger"
        mode="edit"
        selectedTrigger={triggerType}
        isVisible={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSelect={handleSelectTrigger}
      />
    </>
  );
};

const TriggerContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(${SpacingToken.Spacing4});
  padding-left: var(${SpacingToken.Spacing6});
  border-left: 1px dashed var(${DesignToken.BorderBasicPrimary});
  margin-top: var(${SpacingToken.Spacing4});
`;
